/**
 * One place that knows how to rip a pack, so every "Rip now" button on the site
 * behaves identically: check the session, buy, then hand the result to the
 * theatre. Mounting the theatre at the app root rather than per-page means a
 * reveal survives navigation and is never clipped by a card's overflow.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import { useOpenPack, useSellOpen } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import { ApiError, type OpenResult, type PackSummary } from '../lib/api'
import { cents } from '../lib/format'
import { RipTheatre } from './RipTheatre'

interface RipContextValue {
  rip: (pack: PackSummary) => Promise<void>
  isRipping: boolean
  /** The pack currently being bought, so its own button can show the spinner. */
  pendingPackId: string | null
}

const RipContext = createContext<RipContextValue | null>(null)

export function useRip(): RipContextValue {
  const context = useContext(RipContext)
  if (!context) throw new Error('useRip must be used inside <RipProvider>')
  return context
}

export function RipProvider({ children }: { children: ReactNode }) {
  const { user, connectAndSignIn, error: authError, clearError: clearAuthError } = useAuth()
  const openPack = useOpenPack()
  const sellOpen = useSellOpen()

  const [active, setActive] = useState<{ pack: PackSummary; result: OpenResult } | null>(null)
  const [pendingPackId, setPendingPackId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const rip = useCallback(
    async (pack: PackSummary) => {
      setError(null)
      setNotice(null)

      // Signing in is part of ripping, not a separate errand: someone who clicks
      // "Rip now" has already told us what they want.
      if (!user) {
        await connectAndSignIn()
        return
      }

      setPendingPackId(pack.id)
      try {
        const result = await openPack.mutateAsync(pack.id)
        // Fired the moment the purchase itself succeeds, not when the reveal
        // finishes — the charge and the cards are already final by this point,
        // so this is the confirmation that survives even if the theatre below
        // gets closed or navigated away from a second later.
        const n = result.positionIds.length
        setNotice(
          `Bought ${pack.name} for ${cents(Math.round(result.open.price * 100))}. ` +
            `${n} card${n === 1 ? '' : 's'} added to your Vault.`,
        )
        setActive({ pack, result })
      } catch (err) {
        setError(
          err instanceof ApiError && err.code === 'insufficient_balance'
            ? `Not enough balance for ${pack.name}.`
            : err instanceof Error
              ? err.message
              : 'Could not open that pack.',
        )
      } finally {
        setPendingPackId(null)
      }
    },
    [connectAndSignIn, openPack, user],
  )

  const value = useMemo<RipContextValue>(
    () => ({ rip, isRipping: openPack.isPending, pendingPackId }),
    [openPack.isPending, pendingPackId, rip],
  )

  return (
    <RipContext.Provider value={value}>
      {children}

      {(error ?? authError) && (
        <div
          role="alert"
          className="panel fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center justify-between gap-4 px-4 py-3 text-sm sm:inset-x-auto sm:right-4 sm:left-auto"
          style={{ borderColor: 'var(--color-down)' }}
        >
          <span>{error ?? authError}</span>
          <button
            className="btn btn-quiet btn-sm"
            onClick={() => {
              setError(null)
              clearAuthError()
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Sits under the theatre (same z-50, later in DOM order beats it) while
          a reveal is open, and is what's left once it closes — the confirmation
          that a purchase already happened outlives the animation. */}
      {notice && (
        <div
          role="status"
          className="panel fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center justify-between gap-4 px-4 py-3 text-sm sm:inset-x-auto sm:right-4 sm:left-auto"
          style={{ borderColor: 'var(--color-up)' }}
        >
          <span>{notice}</span>
          <button className="btn btn-quiet btn-sm" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      {active && (
        <RipTheatre
          pack={active.pack}
          result={active.result}
          selling={sellOpen.isPending}
          onClose={() => setActive(null)}
          onSellAll={async () => {
            const openId = active.result.open.id
            try {
              const sold = await sellOpen.mutateAsync(openId)
              setError(null)
              setActive(null)
              // A sale is the one action here with no visible landing page, so
              // it gets a confirmation of its own.
              setTimeout(
                () => setError(`Sold ${sold.soldCount} cards for ${cents(sold.proceedsCents)}.`),
                0,
              )
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not sell those cards.')
            }
          }}
        />
      )}
    </RipContext.Provider>
  )
}
